import { forEach, mapValues, values, trim, at } from 'lodash';
import * as Yup from 'yup';
import { TFunction } from 'react-i18next';
import { FetchedFieldsData } from '../../types/form.types';
import { PORTAL_TYPE } from '../../types/app.types';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type,@typescript-eslint/explicit-module-boundary-types
const getFormValidationSchema = (
  fields: FetchedFieldsData,
  portalType: PORTAL_TYPE | undefined,
  t: TFunction
) => {
  const fieldsArray = values(
    mapValues(fields, (value, key) => {
      return value ? { id: key, ...value } : null;
    })
  );

  return fieldsArray.reduce((schema, field) => {
    if (!field) {
      return schema;
    }
    const { id, validation, type } = field;
    let regex: RegExp;

    const typeMap: { [key: string]: 'string' | 'mixed' | 'boolean' } = {
      textbox: 'string',
      textarea: 'string',
      select: 'string',
      checkbox: 'boolean',
      file: 'mixed'
    };

    if (!Yup[typeMap[type]]) {
      return schema;
    }

    // string validation
    let validator = Yup.mixed().typeError('Incorrect type');

    const message = (
      validName: string,
      num?: string | number | boolean | undefined
    ): string => {
      const labelValidName = t(field?.label);
      const messageValidName = t(
        [
          `form.errors.${portalType}.${field.id}.${validName}`,
          `form.errors.${field.id}.${validName}`,
          `form.errors.${validName}`,
          'form.errors.unspecific'
        ],
        {
          label: labelValidName,
          maxlength: num,
          minlength: num
        }
      ).toString();
      return messageValidName;
    };

    const allowedTypes = (
      // eslint-disable-next-line
      validationValue: any,
      context: Yup.TestContext
    ): {
      validationTypes: string[];
      validationMessage: Yup.ValidationError;
    } => {
      const { createError } = context;
      const validationTypes = validationValue;
      let validationMessage = t('form.errors.allowed_types');
      validationMessage += ' ';
      validationMessage += validationTypes
        .join(', ')
        .replace(/application\/|image\//gi, '')
        .replace('vnd.ms-excel', 'xlsx')
        .replace(
          'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'xls'
        );

      return {
        validationTypes,
        validationMessage: createError({ message: validationMessage })
      };
    };

    const conditional = (
      // eslint-disable-next-line
      validName: string,
      context: Yup.TestContext
    ): string | boolean | number => {
      const { parent } = context;
      const { case_reason, case_detailed_reason } = parent;
      const [validationValue, validationConditional] = at(
        validation,
        validName,
        `conditional.${case_reason}.${
          case_detailed_reason || '_default'
        }.${validName}`
      );

      return validationConditional === undefined
        ? validationValue
        : validationConditional;
    };

    forEach(validation, (validationValue, key) => {
      if (validationValue) {
        switch (key) {
          case 'required':
            if (
              validationValue === true &&
              field.id === 'case_detailed_reason'
            ) {
              validator = validator.when('case_reason', {
                is: (val: string) =>
                  fields.case_detailed_reason?.options &&
                  !Array.isArray(fields.case_detailed_reason.options) &&
                  fields.case_detailed_reason.options[val],
                then: Yup.mixed().required(message('required')),
                otherwise: Yup.mixed().notRequired()
              });
            } else {
              validator = validator.test(
                key,
                message('required'),
                function dummyConditional(fieldValue) {
                  if (conditional('required', this)) {
                    return field.type === 'checkbox'
                      ? fieldValue === true
                      : fieldValue?.length > 0;
                  }
                  return true;
                }
              );
            }
            break;
          case 'maxlength':
            validator = validator.test(
              key,
              message('maxlength', validationValue),
              function dummyConditional(fieldValue) {
                return !(
                  fieldValue?.trim().length > conditional('maxlength', this)
                );
              }
            );

            break;
          case 'minlength':
            validator = validator.test(
              key,
              message('minlength', validationValue),
              function dummyConditional(fieldValue) {
                return !(
                  fieldValue?.trim().length < conditional('minlength', this) &&
                  fieldValue?.trim().length > 0
                );
              }
            );
            break;
          case 'pattern':
            validator = validator.test(
              key,
              message('pattern'),
              function dummyConditional(fieldValue) {
                regex = new RegExp(
                  (conditional('pattern', this) as unknown) as RegExp
                );
                return fieldValue?.length ? regex.test(trim(fieldValue)) : true;
              }
            );
            break;
          case 'allowed_types':
            validator = validator.test(
              key,
              message('allowed_types'),
              function dummyTypes(fieldValue) {
                const { validationMessage, validationTypes } = allowedTypes(
                  validationValue,
                  this
                );
                return !fieldValue?.length ||
                  (fieldValue?.length &&
                    validationTypes.includes(fieldValue[0]?.type) &&
                    fieldValue[0]?.name.split('.').pop() !== 'csv')
                  ? true
                  : validationMessage;
              }
            );
            break;
          case 'max_size_kb':
            if (typeof validationValue === 'number') {
              validator = validator.test(
                key,
                message('max_size_kb'),
                (fieldValue) => {
                  return (
                    !fieldValue?.length ||
                    (fieldValue?.length &&
                      fieldValue[0]?.size < validationValue * 1024)
                  );
                }
              );
            }
            break;
          default:
            break;
        }
      }
    });
    return schema.concat(Yup.object().shape({ [id]: validator }));
  }, Yup.object().shape({}));
};

export default getFormValidationSchema;
